<script setup lang="ts">
import { onMounted, onBeforeUnmount, ref } from 'vue';
import VirtualScroller from 'primevue/virtualscroller';
import ActivityLogItem from '@/components/ActivityLog/ActivityLogItem.vue';
import { useActivityLogStore } from '@/store';
import { can, useDateTime, useRequestCancel, useRouting } from '@/composables';
import { PERMISSIONS, REQUEST } from '@/enum';
import { storeToRefs } from 'pinia';

const dateUtils = useDateTime();
const dates = ref<Date[] | null>([]);
const activityLogStore = useActivityLogStore();
const { logs, loading } = storeToRefs(activityLogStore);
const $routing = useRouting();
const searchFilter = ref();

onMounted(async () => {
  activityLogStore.restorePaginationState(true);
  defineDefaultDates();
  await init();
});

const init = async () => {
  defineDateQueryParams();
  await getLogs();
};

const defineDefaultDates = () => {
  const end = new Date();
  const start = new Date(end.getTime() - 28 * 24 * 60 * 60 * 1000);
  const defaultDates: Date[] = [start, end];

  dates.value = defaultDates;
  activityLogStore.filters.start_at = dateUtils.formatDateForApi(start);
  activityLogStore.filters.end_at = dateUtils.formatDateForApi(end);
};

const defineDateQueryParams = () => {
  if (dates.value && dates.value.length > 1) {
    activityLogStore.filters.start_at = dateUtils.formatDateForApi(dates.value[0]);
    activityLogStore.filters.end_at = dateUtils.formatDateForApi(dates.value[1]);

    $routing.addQueryParams({
      start_at: activityLogStore.filters.start_at,
      end_at: activityLogStore.filters.end_at,
    });
  }
};

const onDateRangeChange = async (event: Date[]) => {
  activityLogStore.restorePaginationState(true);
  dates.value = event;
  defineDateQueryParams();
  await getLogs();
};

const onSearch = async (search: string) => {
  const hasSearchTerm = search.length >= 3;

  if (hasSearchTerm) {
    activityLogStore.filters.search = search;
    await init();
  }
};

onBeforeUnmount(() => useRequestCancel().requestSaverAbort(REQUEST.LOGS));

const onLazyLoadLogs = async (event) => {
  const difference = event.last - event.first;

  if (difference >= 30 && difference <= 35) {
    await getLogs();
  }
};

const getLogs = async () => {
  await activityLogStore.getLogs(Number($routing.route.params.id));
};
</script>

<template>
  <Card>
    <template #header>
      <h2>Activity Log</h2>
    </template>
    <template #content>
      <div class="grid">
        <div class="col-fit p-2">
          <AppDateRangePicker
            v-if="can([PERMISSIONS.PERSON_LOGS_INDEX_FILTER_DATE])"
            v-model="dates"
            class="flex-1 mr-3 mb-3 min-w-min w-full"
            :value="dates"
            @change="onDateRangeChange"
          />
        </div>
        <div v-if="can([PERMISSIONS.PERSON_LOGS_INDEX_SEARCH])" class="col-5">
          <IconField icon-position="left">
            <InputIcon class="p-input-icon-left" style="z-index: 100">
              <Icon name="search" class="text-lg" />
            </InputIcon>
            <InputText
              v-if="can([PERMISSIONS.PERSON_LOGS_INDEX_SEARCH])"
              v-model="searchFilter"
              v-debounce:600ms="onSearch"
              class="w-full pl-5"
              placeholder="Search"
              :min-length="3"
            />
          </IconField>
        </div>
        <div class="col text-right">
          <Spinner v-if="loading" :size="25" />
        </div>
      </div>
      <div v-if="logs.length > 0" class="overflow-y-none scrollbar flex-1 px-0">
        <VirtualScroller :items="logs" :item-size="50" :lazy="true" scroll-height="550px" @lazy-load="onLazyLoadLogs">
          <template #item="{ item }">
            <ActivityLogItem
              :key="item.id"
              :access-tenant="item.access_tenant"
              :subject="item.subject"
              :description="item.description"
              :subject-type="item.subject_type"
              :causer="item.causer"
              :impersonated-by="item.impersonated_by"
              :event="item.event"
              :properties="item.properties"
              :custom="item.custom"
              :time-ago="item.updated_at"
              :changes="item.changes"
            />
          </template>
        </VirtualScroller>
      </div>
      <div v-if="!loading && !logs.length" class="bg-white p-3 my-3 border-round-sm text-sm mt-5">
        <p class="text-center">No activity was found.</p>
      </div>
    </template>
  </Card>
</template>

<style scoped lang="scss">
.daterangepicker-container {
  max-width: none !important;
  :deep(.p-inputtext) {
    font-weight: bold;
    padding-left: 30px;
  }
}

:deep(.p-dropdown) {
  .p-dropdown-label {
    font-weight: bold;
    &.p-placeholder {
      color: $grayDark;
    }
  }
}
</style>
